import { useState } from 'react';
import { Check } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Button, Dialog, FileField, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { archiveDocument } from '../api';
import type { WizardUploadRow } from '../types';
import type { LocalUpload } from './useUploadQueue';
import styles from './wizard.module.css';

const VISIBLE_ROWS = 8;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.dwg,application/pdf,image/jpeg,image/png,image/vnd.dwg';

interface FileRowModel {
  key: string;
  name: string;
  kind: string;
  sizeLabel: string | null;
  state: 'uploaded' | 'progress' | 'queued' | 'error';
  progress: number;
  stateLabel: string;
  error: string | null;
  retry: (() => void) | null;
}

function kindOf(row: WizardUploadRow): string {
  const dot = row.name.lastIndexOf('.');
  return dot >= 0
    ? row.name
        .slice(dot + 1)
        .slice(0, 4)
        .toLowerCase()
    : row.kind.toLowerCase();
}

export function fileRowsOf(
  local: LocalUpload[],
  server: WizardUploadRow[],
  onRetry: (id: string) => void,
): FileRowModel[] {
  const localRows = local.map<FileRowModel>((item) => ({
    key: item.id,
    name: item.name,
    kind: item.kind,
    sizeLabel: item.sizeLabel,
    state:
      item.status === 'done'
        ? 'uploaded'
        : item.status === 'uploading'
          ? 'progress'
          : item.status === 'queued'
            ? 'queued'
            : 'error',
    progress: item.progress,
    stateLabel: item.status === 'done' ? 'hochgeladen' : item.status === 'queued' ? 'in Warteschlange' : '',
    error: item.error,
    retry: item.status === 'error' ? () => onRetry(item.id) : null,
  }));
  const serverRows = server.map<FileRowModel>((row) => ({
    key: row.documentId,
    name: row.name,
    kind: kindOf(row),
    sizeLabel: row.sizeLabel,
    state: row.state === 'uploaded' ? 'uploaded' : row.state === 'queued' ? 'queued' : 'error',
    progress: 100,
    stateLabel: row.stateLabel,
    error: row.error,
    retry: null,
  }));
  return [...localRows, ...serverRows];
}

export interface UploadsMainProps {
  propertyId: string;
  local: LocalUpload[];
  uploads: WizardUploadRow[];
  onFiles: (files: File[]) => void;
  onRetry: (id: string) => void;
  onChanged: () => void;
}

/** Objekt anlegen 02: the dropzone and the file list with per-row states. */
export function UploadsMain({ propertyId, local, uploads, onFiles, onRetry, onChanged }: UploadsMainProps) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const rows = fileRowsOf(local, uploads, onRetry);
  const uploaded = rows.filter((row) => row.state === 'uploaded').length;
  const running = rows.filter((row) => row.state === 'progress' || row.state === 'queued').length;
  const errors = rows.filter((row) => row.state === 'error').length;
  const visible = expanded ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visible.length;
  const removable = uploads.filter((row) => row.origin === 'upload');

  async function removeAll() {
    setRemoving(true);
    try {
      for (const row of removable) await archiveDocument(row.documentId);
      toast({ message: `${removable.length} ${removable.length === 1 ? 'Datei' : 'Dateien'} entfernt.` });
      setConfirmRemove(false);
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setRemoving(false);
    }
  }

  const summary = [`${uploaded} hochgeladen`, `${running} laufen`, `${errors} Fehler`].join(' · ');

  return (
    <>
      <FileField
        label="Unterlagen hochladen"
        labelHidden
        title="Alle Unterlagen auf einmal hierher ziehen"
        description="PDF, Word, Excel, Bilder oder ein ZIP-Archiv. Sie müssen nichts benennen oder sortieren."
        buttonLabel="Dateien auswählen"
        accept={ACCEPT}
        multiple
        onFiles={onFiles}
        name={`uploads-${propertyId}`}
      />
      <section className={clsx(styles.card, styles.filesCard)} aria-label="Dateien">
        <div className={styles.filesHead}>
          <span className={styles.filesCount}>
            {rows.length} {rows.length === 1 ? 'Datei' : 'Dateien'}
          </span>
          <span className={styles.filesSummary}>{summary}</span>
          {removable.length > 0 ? (
            <span className={styles.filesEnd}>
              <button type="button" className={styles.linkButton} onClick={() => setConfirmRemove(true)}>
                Alle entfernen
              </button>
            </span>
          ) : null}
        </div>
        {rows.length === 0 ? (
          <div className={styles.filesMore}>
            Noch keine Dateien. Ziehen Sie Unterlagen in das Feld oben oder wählen Sie Dateien aus.
          </div>
        ) : (
          <div className={styles.fileRows}>
            {visible.map((row) => (
              <div
                key={row.key}
                className={clsx(styles.fileRow, row.state === 'error' && styles.fileRowError)}
              >
                <span className={styles.fileIcon} aria-hidden="true">
                  {row.kind}
                </span>
                <span className={styles.fileText}>
                  <span className={styles.fileName}>{row.name}</span>
                  {row.sizeLabel ? <span className={styles.fileMeta}>{row.sizeLabel}</span> : null}
                </span>
                {row.state === 'uploaded' ? (
                  <span className={clsx(styles.fileState, styles.fileStateDone)}>
                    <span className={styles.checkCircle} aria-hidden="true">
                      <Check size={11} strokeWidth={2.5} aria-hidden="true" />
                    </span>
                    {row.stateLabel}
                  </span>
                ) : row.state === 'progress' ? (
                  <span
                    className={styles.fileProgress}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={row.progress}
                    aria-label={row.name}
                  >
                    <span className={styles.progressTrack}>
                      <span className={styles.progressFill} style={{ width: `${row.progress}%` }} />
                    </span>
                    <span className={styles.progressPct}>{row.progress} %</span>
                  </span>
                ) : row.state === 'queued' ? (
                  <span className={styles.fileState}>{row.stateLabel || 'in Warteschlange'}</span>
                ) : (
                  <span className={styles.fileErrorActions}>
                    <span className={styles.fileErrorText}>{row.error ?? 'Fehler'}</span>
                    {row.retry ? (
                      <button
                        type="button"
                        className={clsx('pressable', styles.smallButton)}
                        onClick={row.retry}
                      >
                        Erneut versuchen
                      </button>
                    ) : null}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {hiddenCount > 0 ? (
          <button
            type="button"
            className={clsx(styles.linkButton, styles.filesMore)}
            onClick={() => setExpanded(true)}
          >
            {hiddenCount} weitere hochgeladen
          </button>
        ) : expanded && rows.length > VISIBLE_ROWS ? (
          <button
            type="button"
            className={clsx(styles.linkButton, styles.filesMore)}
            onClick={() => setExpanded(false)}
          >
            Weniger anzeigen
          </button>
        ) : null}
      </section>
      <Dialog
        open={confirmRemove}
        onClose={() => (removing ? undefined : setConfirmRemove(false))}
        eyebrow="Unterlagen"
        title="Alle Dateien entfernen?"
        subtitle={`${removable.length} ${removable.length === 1 ? 'hochgeladene Datei wird' : 'hochgeladene Dateien werden'} aus der Objektakte entfernt. Unterlagen vom Bauträger bleiben erhalten.`}
        width={480}
        tone="danger"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)} disabled={removing}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={removing} onClick={() => void removeAll()}>
              Entfernen
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
    </>
  );
}
